import React, { useEffect, useRef, useState } from 'react';
import { ChevronUp, ChevronDown, GripVertical, Plus } from 'lucide-react';
import GtmBin from './GtmBin';
import GtmPickInput from './GtmPickInput';

/* ONE LIST, TWO HALVES, AND THE ORDER IS THE ANSWER.

   Max, 2026-10-07: "thứ tự sẽ tương ứng với mức độ quan trọng luôn. Do đó sẽ có tính năng kéo line
   di chuyển để sắp xếp" — and the same shape serves Q1's ten personas and Q2's eleven channels,
   so it is written once. Two copies of "drag to rank" is two places for a drag to mean something
   different.

   EVERY ROW ALWAYS EXISTS. The document lists ten personas and rules eight out, eleven channels
   and rules seven out, each with its reason — that is the record that all of them were considered.
   So nothing is added or removed to express a decision: a row moves across the divider. Above it
   is in, in priority order; below it is out, and keeps its note.

   DRAG IS NOT THE ONLY WAY. A control reachable only by dragging is reachable by nobody on a
   keyboard, and this codebase has already lost a click to something drawn on top of it seven
   times — so each row also carries ↑ and ↓ buttons, and they are the thing the check presses. */
export default function GtmRankList({
  rows, idKey, canEdit, t, onSave, onAdd, onRemove, renderRow,
  headName, headNote, aboveLabel, belowLabel, addLabel, busy,
  /* ONE HALF. The launch order has no "not in this programme" — every step is in it — so the
     divider, the second heading and the is_target a drag would carry all stand down, and the
     number means only the position. Nothing else about the list changes. */
  singleHalf = false, nameKey = 'name', noteKey = 'note', bulk = false, onEdits, leading,
  /* A TICK TO SELECT — So-yeon Shin, 2026-10-16: "Please make Persona selectable from a list of
     options", and the same for the 11 channels. Dragging a row across the divider always did
     this, and nobody could tell; the tick says it in one press. Ticking puts the row at the
     BOTTOM of the selected half (it is the newest priority, not the first); unticking puts it at
     the top of the rest. The same order write either way. */
  selectable = false, selectLabel, selectHint,
  /* A LIST TO PICK FROM WHEN ADDING — Max, 2026-10-16, on the persona box: "có thể select từ những
     cái có sẵn cái nào đã chọn thì ko hiện có thể type giá trị mới và add vào để lần sau dùng".
     `catalog` is every name on the shared list; the ones this list already holds are left out, and
     a name typed that is not on it is added by the server and offered next time. The choices are a
     DROPDOWN under the box (GtmPickInput; Max: "làm dropdown chứ ko phải tab"), a portal fixed to the
     box because `.gtm-rank` clips. */
  catalog = null, catalogNewHint, catalogEmpty, catalogDup,
}) {
  /* EDIT THE TABLE — Max, 2026-10-07: "ko lẹ mỗi lần sửa phải click từng cái chời". Ten rows meant
     ten modals. Unlocked, every row's two cells are inputs and ONE Save writes the lot; the same
     shape Daily Output keeps, down to the count on the button. `edits` holds only what has been
     typed, keyed by id — not a copy of the table — so a reload underneath cannot wipe an edit in
     progress, and a row nobody touched is not written at all. */
  /* THE LOCK IS THE SHEET'S, NOT THIS LIST'S — Max, 2026-10-07: "tôi muốn nút edit apply cho
     toàn bộ phiếu". Three lists each with their own "Edit the table" was three answers to one
     question; `bulk` arrives as a prop now and the toolbar went with it. */
  const [edits, setEdits] = useState({});
  /* ADD IS A ROW, NOT A DIALOG — Max, 2026-10-07: "ko cần pop up nha cho điền trực tiếp luôn là
     add line thì sẽ hiện ra các trường cần điền trên dòng đó". It lands on the list's own grid so
     the two boxes sit under the two columns they fill, and what you are typing is a row in the
     table you are looking at rather than a copy of it floating over the top. */
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newNote, setNewNote] = useState('');
  const noteRef = useRef(null);
  const low = (v) => String(v || '').trim().toLowerCase();
  /* "cái nào đã chọn thì ko hiện": on a list with a Select tick, what is hidden is what is
     SELECTED — the unselected rows are offered with the rest of the shared list, and adding one
     ticks it (the server puts it at the end of the selected half). */
  const isIn = (r) => (selectable ? !!r.is_target : true);
  const have = new Set(rows.filter(isIn).map((r) => low(r[nameKey])));
  const pool = [...(catalog || []), ...rows.filter((r) => !isIn(r)).map((r) => r[nameKey] || '')];
  const avail = pool.filter((n, i) => n && !have.has(low(n)) && pool.findIndex((m) => low(m) === low(n)) === i);
  const q = low(newName);
  const isDup = !!q && have.has(q);
  /* "New" is said only once nothing on the list matches what is typed — while one does, the
     choice under the box is the clearer answer. */
  const isNew = !!q && !isDup && !pool.some((n) => low(n).includes(q));
  const addNow = () => {
    if (!newName.trim() || isDup) return;
    onAdd({ name: newName.trim(), note: newNote.trim(), ...(catalog && selectable ? { select: true } : {}) });
    setNewName(''); setNewNote(''); setAdding(false);
  };
  const dirtyIds = Object.keys(edits).filter((id) => {
    const r = rows.find((x) => String(x[idKey]) === id);
    return r && (edits[id].name !== (r[nameKey] || '') || edits[id].note !== (r[noteKey] || ''));
  });
  useEffect(() => { if (!bulk) setEdits({}); }, [bulk]);
  useEffect(() => { if (onEdits) onEdits(dirtyIds.map((id) => ({ id: Number(id), ...edits[id] }))); });
  const cellOf = (r, f) => edits[String(r[idKey])]?.[f]
    ?? (r[f === 'name' ? nameKey : noteKey] || '');
  const typeIn = (r, f, v) => setEdits((p) => ({
    ...p,
    [String(r[idKey])]: {
      name: p[String(r[idKey])]?.name ?? (r[nameKey] || ''),
      note: p[String(r[idKey])]?.note ?? (r[noteKey] || ''),
      [f]: v,
    },
  }));
  /* The list is held locally while it is being dragged and pushed on drop: a save per step would
     write four times for one four-row move, and a half-applied order is a different order rather
     than a missing one. */
  const [list, setList] = useState(rows);
  const [from, setFrom] = useState(null);
  const [over, setOver] = useState(null);
  const dirty = useRef(false);

  /* A reload (or somebody else's save) replaces the list — but never mid-drag, which would pull
     the row out from under the hand. */
  useEffect(() => {
    if (from === null && !dirty.current) setList(rows);
  }, [rows, from]);

  const commit = (next) => {
    setList(next);
    dirty.current = true;
    onSave(next.map((r) => ({ id: r[idKey], is_target: singleHalf || !!r.is_target })))
      .finally(() => { dirty.current = false; });
  };

  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= list.length) return;
    const next = list.slice();
    const [row] = next.splice(i, 1);
    /* MOVING ACROSS THE DIVIDER IS WHAT CHANGES THE DECISION, and it is the same gesture as
       re-ranking — the row takes the half it lands in. Worked out from the NEIGHBOUR it displaces
       rather than from a count, so the first row of the lower half can be dragged up into the
       upper one and mean "this is a target now". */
    if (!singleHalf) {
      const neighbour = delta < 0 ? next[j] : next[j - 1];
      row.is_target = neighbour ? !!neighbour.is_target : !!row.is_target;
    }
    next.splice(j, 0, row);
    commit(next);
  };

  const toggle = (i) => {
    const next = list.slice();
    const [row] = next.splice(i, 1);
    const sel = !row.is_target;
    const moved = { ...row, is_target: sel };
    const cut = next.findIndex((r) => !r.is_target);
    next.splice(cut === -1 ? next.length : cut, 0, moved);
    commit(next);
  };

  const drop = (to) => {
    if (from === null || to === from) { setFrom(null); setOver(null); return; }
    const next = list.slice();
    const [row] = next.splice(from, 1);
    const at = from < to ? to - 1 : to;
    if (!singleHalf) {
      const neighbour = next[at] || next[at - 1];
      row.is_target = neighbour ? !!neighbour.is_target : !!row.is_target;
    }
    next.splice(at, 0, row);
    setFrom(null); setOver(null);
    commit(next);
  };

  /* The divider is drawn BEFORE the first row that is not a target, and at the very end when
     everything is in — an empty "out of scope" half with no line above it would read as a list
     that simply stopped. */
  const firstOut = singleHalf ? -1 : list.findIndex((r) => !r.is_target);
  let rank = 0;

  return (
    <>
    {selectable && canEdit && selectHint && <p className="gtm-rank-hint">{selectHint}</p>}
    <div className={`gtm-rank${busy ? ' busy' : ''}${selectable && canEdit ? ' sel' : ''}`}>
      {/* The slide names its two columns; so does this, once, at the top — a two-column list
          with no headings makes the reader work out what the second one is. */}
      <div className={`gtm-rankcols${selectable && canEdit ? ' sel' : ''}`}>
        <span className="gtm-rankno" />
        {selectable && canEdit && <span className="gtm-ranksel-h">{selectLabel}</span>}
        <span>{headName}</span>
        <span>{headNote}</span>
      </div>
      <div className="gtm-rank-head">{aboveLabel}</div>
      {list.map((r, i) => {
        const open = singleHalf || r.is_target ? ++rank : null;
        return (
          <React.Fragment key={r[idKey]}>
            {i === firstOut && <div className="gtm-rank-head out">{belowLabel}</div>}
            <div
              className={`gtm-rankrow${r.is_target ? ' on' : ''}${selectable && canEdit ? ' sel' : ''}`
                + (from === i ? ' dragging' : '') + (over === i ? ' over' : '')}
              draggable={canEdit}
              onDragStart={() => setFrom(i)}
              onDragOver={(e) => { if (from !== null) { e.preventDefault(); setOver(i); } }}
              onDragEnd={() => { setFrom(null); setOver(null); }}
              onDrop={(e) => { e.preventDefault(); drop(i); }}
            >
              {canEdit && <GripVertical size={14} className="gtm-grip" aria-hidden="true" />}
              {/* The rank is the POSITION, printed, never typed. A blank here is not a missing
                  number — it is a row that is not in this programme, which the half it sits in
                  already says in words. */}
              <span className="gtm-rankno">{open ?? ''}</span>
              {selectable && canEdit && (
                <span className="gtm-ranksel">
                  <input type="checkbox" checked={!!r.is_target} disabled={busy}
                    aria-label={`${selectLabel}: ${r[nameKey] || ''}`} title={selectLabel}
                    onChange={() => toggle(i)} />
                </span>
              )}
              {/* TWO COLUMNS, as the document draws it — Max, 2026-10-07: "phần audience thì
                  tôi nghĩ là nên thành 2 cột á". Name and "in this programme" each have their own
                  track, so the descriptions start on one line down the whole list instead of
                  wherever the name happened to end. */}
              {bulk ? (
                <>
                  <span className="gtm-rankcell">
                    {/* Whatever the row draws BEFORE its name — the channel's company status dot —
                        stays while the name is being typed. It vanished in bulk mode, which took
                        the one fact the editor cannot change off the screen exactly when somebody
                        was working on that row. */}
                    {leading ? leading(r) : null}
                    <input className="gtm-rankname bulk" value={cellOf(r, 'name')}
                      aria-label={headName}
                      onChange={(e) => typeIn(r, 'name', e.target.value)} />
                  </span>
                  <input className="gtm-ranknote bulk" value={cellOf(r, 'note')}
                    aria-label={headNote} placeholder={headNote}
                    onChange={(e) => typeIn(r, 'note', e.target.value)} />
                </>
              ) : renderRow(r, open)}
              {/* THE MOVERS STAY WHILE THE SHEET IS OPEN. They used to hide in bulk mode, from
                  when bulk was a mode of its own — now bulk IS "unlocked", so hiding them left
                  no way to reorder at all. Dragging and typing do coexist: the drag writes as it
                  happens and the cells wait for Save, which is what the bar says. */}
              {canEdit && (
                <span className="gtm-rankmove">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                    title={t('gtm.moveUp')} aria-label={t('gtm.moveUp')}>
                    <ChevronUp size={13} />
                  </button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1}
                    title={t('gtm.moveDown')} aria-label={t('gtm.moveDown')}>
                    <ChevronDown size={13} />
                  </button>
                  {/* A ROW FROM THE DOCUMENT HAS NO BIN, and is not merely refused on press: the
                      list IS the record that all ten were considered, so removing one removes a
                      decision. Moving it out of the programme is the gesture that says that. */}
                  {/* EVERY row carries it now, the document's included — Max, 2026-10-08. The
                      bin asks before it fires, and on a masterplan row it says so. */}
                  <GtmBin t={t} busy={busy} fromDoc={!!r.from_doc}
                    label={t('gtm.removeRow')} onRemove={() => onRemove(r)} />
                </span>
              )}
            </div>
          </React.Fragment>
        );
      })}
      {firstOut === -1 && !singleHalf && <div className="gtm-rank-head out">{belowLabel}</div>}
      {/* ADDING LANDS AT THE BOTTOM, outside the programme. A new row arriving already ranked
          would be the screen deciding something nobody typed. */}
      {/* ADD BELONGS TO THE OPEN SHEET. This read `canEdit && !bulk` from when bulk was a mode of
          its own — with bulk now meaning "unlocked", it hid the button exactly when somebody was
          editing, and locked there is no button either. So a row could never be added at all. */}
      {canEdit && (adding ? (
        <>
          <div className="gtm-rankadd-lab">{addLabel}</div>
          <div className="gtm-rankadd">
            <span />
            {catalog ? (
              <GtmPickInput autoFocus value={newName} onChange={setNewName} options={avail}
                placeholder={headName} ariaLabel={headName} emptyText={catalogEmpty}
                onPick={(n) => { setNewName(n); noteRef.current?.focus(); }}
                onEnter={addNow} onEscape={() => setAdding(false)} />
            ) : (
              <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder={headName} aria-label={headName}
                onKeyDown={(e) => { if (e.key === 'Escape') setAdding(false); if (e.key === 'Enter') addNow(); }} />
            )}
            <input ref={noteRef} value={newNote} onChange={(e) => setNewNote(e.target.value)}
              placeholder={headNote} aria-label={headNote}
              onKeyDown={(e) => { if (e.key === 'Escape') setAdding(false); if (e.key === 'Enter') addNow(); }} />
            <button type="button" className="go" disabled={!newName.trim() || isDup} onClick={addNow}>{t('gtm.add')}</button>
            <button type="button" onClick={() => setAdding(false)}>{t('gtm.cancel')}</button>
          </div>
          {catalog && (isDup || isNew) && (
            <div className={`gtm-rankpick-note${isDup ? ' bad' : ''}`}>{isDup ? catalogDup : catalogNewHint}</div>
          )}
        </>
      ) : (
        <button type="button" className="gtm-rankadd-btn" onClick={() => setAdding(true)}>
          <Plus size={12} /> {addLabel}
        </button>
      ))}
    </div>
    </>
  );
}
